import React from "react";

import { Check, X } from "lucide-react";

import { activateOnEnterOrSpace } from "../utils/keyboardActivation";

const PremiumOverlay = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const features = [
    "Create & Manage Playlists",
    "Unlocks All App Features",
    "Manage Your Content",
    "Fast Login w/ LunaKeys™",
    "Access To Support",
    "Custom User Profiles",
  ];

  const plans = [
    {
      id: "trial",
      name: "Free Trial",
      description:
        "24hrs trial, will renew after free trial ends unless canceled.",
      price: "$0.00",
      period: "/month",
      note: "this is a recurring subscription",
      url: "https://dashboard.lunova.app/register?plan=trial",
      featured: false,
    },
    {
      id: "monthly",
      name: "Monthly",
      description:
        "Start with our monthly subscription and enjoy total access.",
      price: "$1.99",
      period: "/month",
      note: "this is a recurring subscription",
      url: "https://dashboard.lunova.app/register?plan=monthly",
      featured: true,
    },
    {
      id: "quarterly",
      name: "Quarterly",
      description:
        "Start with our quarterly subscription and enjoy total access.",
      price: "$5.97",
      period: "/3 months",
      note: "this is a recurring subscription",
      url: "https://dashboard.lunova.app/register?plan=quarterly",
      featured: false,
    },
  ];

  const handleSelectPlan = (url) => {
    window.open(url, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-8">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#1a1d24]/95 backdrop-blur-sm"
        role="presentation"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 max-w-5xl w-full mx-4">
        {/* Close button */}
        {onClose && (
          <button
            className="absolute -top-2 -right-2 z-20 bg-gray-800 rounded-full p-2 text-gray-400 hover:text-white transition-colors"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        )}

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-center text-white mb-2">
          Ready To Level Up?
        </h1>
        <p className="text-gray-400 text-center text-lg mb-10">
          Experience Streaming on Your Terms
        </p>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center mb-8">
          {plans.map((plan) => (
            <div
              className={`relative rounded-xl transition-all cursor-pointer hover:scale-[1.02] ${
                plan.featured
                  ? "bg-[#1e2230] border-2 border-[#6495ED] p-8 md:scale-105 shadow-lg shadow-[#6495ED]/20 hover:border-[#89b4fa] hover:shadow-xl hover:shadow-[#6495ED]/40"
                  : "bg-[#1e2230]/60 border border-gray-700/50 p-6 hover:border-gray-400 hover:shadow-lg hover:shadow-white/10"
              }`}
              key={plan.id}
              role="button"
              tabIndex={0}
              onClick={() => handleSelectPlan(plan.url)}
              onKeyDown={activateOnEnterOrSpace}
            >
              {/* Most Popular badge */}
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#6495ED] text-white text-xs font-bold px-4 py-1 rounded-full">
                  Most Popular
                </div>
              )}

              {/* Plan name */}
              <h3
                className={`font-bold mb-3 ${
                  plan.featured
                    ? "text-white text-2xl"
                    : "text-gray-200 text-xl"
                }`}
              >
                {plan.name}
              </h3>

              {/* Description */}
              <p
                className={`text-sm mb-6 leading-relaxed ${
                  plan.featured ? "text-gray-300" : "text-gray-500"
                }`}
              >
                {plan.description}
              </p>

              {/* Price */}
              <div className="mb-2">
                <span
                  className={`font-bold ${
                    plan.featured
                      ? "text-white text-5xl"
                      : "text-gray-200 text-4xl"
                  }`}
                >
                  {plan.price}
                </span>
                <span
                  className={`${
                    plan.featured
                      ? "text-gray-400 text-xl"
                      : "text-gray-500 text-lg"
                  }`}
                >
                  {plan.period}
                </span>
              </div>

              {/* Recurring note */}
              <p
                className={`text-xs mb-6 ${
                  plan.featured ? "text-gray-500" : "text-gray-600"
                }`}
              >
                {plan.note}
              </p>

              {/* Features list */}
              <div className="space-y-3">
                {features.map((feature, idx) => (
                  <div className="flex items-center gap-3" key={idx}>
                    <Check
                      className={`flex-shrink-0 ${
                        plan.featured ? "text-[#6495ED]" : "text-gray-500"
                      }`}
                      size={18}
                    />
                    <span
                      className={`text-sm ${
                        plan.featured ? "text-gray-200" : "text-gray-500"
                      }`}
                    >
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <p className="text-gray-500 text-sm text-center max-w-2xl mx-auto">
          Note: Premium access does not automatically include movies (VOD) or TV
          shows. Availability depends on your service provider.
        </p>
      </div>
    </div>
  );
};

export default PremiumOverlay;
